import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, AlertTriangle, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, SlowResponseDetails, SlowResponseFix } from "@/types/technicalAudit";

interface SlowResponseCardProps {
	issue: PageIssue;
}

function getResponseTimeColor(ms: number): { bar: string; text: string } {
	if (ms <= 1000) return { bar: "bg-green-500", text: "text-green-700 dark:text-green-400" };
	if (ms <= 3000) return { bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-400" };
	return { bar: "bg-red-500", text: "text-red-700 dark:text-red-400" };
}

function formatMs(ms: number): string {
	if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
	return `${ms}ms`;
}

export function SlowResponseCard({ issue }: SlowResponseCardProps) {
	const details = issue.issue_details as SlowResponseDetails;
	const proposedFix = issue.proposed_fix as SlowResponseFix | null;
	const colors = getResponseTimeColor(details.response_time_ms);
	const maxDisplay = Math.max(details.threshold_ms * 2, details.response_time_ms * 1.2);
	const percentage = Math.min((details.response_time_ms / maxDisplay) * 100, 100);
	const thresholdPercent = (details.threshold_ms / maxDisplay) * 100;

	return (
		<div className="space-y-4">
			{/* Warning Banner */}
			<div className={cn(
				"flex items-start gap-3 rounded-xl p-4 border",
				details.response_time_ms > 3000
					? "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800"
					: "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800",
			)}>
				<div className={cn(
					"flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
					details.response_time_ms > 3000 ? "bg-red-100 dark:bg-red-900/40" : "bg-amber-100 dark:bg-amber-900/40",
				)}>
					<AlertTriangle className={cn(
						"w-5 h-5",
						details.response_time_ms > 3000 ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400",
					)} />
				</div>
				<div className="flex-1">
					<h4 className={cn(
						"font-semibold",
						details.response_time_ms > 3000 ? "text-red-900 dark:text-red-300" : "text-amber-900 dark:text-amber-300",
					)}>
						Slow Server Response
					</h4>
					<p className={cn(
						"text-sm mt-1",
						details.response_time_ms > 3000 ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400",
					)}>
						This page took {formatMs(details.response_time_ms)} to respond, exceeding the {formatMs(details.threshold_ms)} threshold.
						Slow response times hurt user experience and Core Web Vitals scores.
					</p>
				</div>
			</div>

			{/* Response Time Card */}
			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Response Time
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					<div className="flex items-center justify-between">
						<span className={cn("text-3xl font-bold", colors.text)}>
							{formatMs(details.response_time_ms)}
						</span>
						<span className="text-sm text-muted-foreground">
							Threshold: {formatMs(details.threshold_ms)}
						</span>
					</div>

					{/* Progress Bar with Threshold Marker */}
					<div className="relative">
						<div className="w-full h-3 bg-muted rounded-full overflow-hidden">
							<div
								className={cn("h-full rounded-full transition-all", colors.bar)}
								style={{ width: `${percentage}%` }}
							/>
						</div>
						{/* Threshold marker */}
						<div
							className="absolute top-0 w-0.5 h-5 -mt-1 bg-gray-600"
							style={{ left: `${thresholdPercent}%` }}
						/>
						<div
							className="absolute top-5 text-xs text-muted-foreground -translate-x-1/2"
							style={{ left: `${thresholdPercent}%` }}
						>
							threshold
						</div>
					</div>

					{/* Legend */}
					<div className="flex items-center justify-center gap-4 text-xs text-muted-foreground mt-2">
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-green-500" />
							&lt;1s
						</span>
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-amber-500" />
							1-3s
						</span>
						<span className="flex items-center gap-1">
							<span className="w-2 h-2 rounded-full bg-red-500" />
							&gt;3s
						</span>
					</div>
				</CardContent>
			</Card>

			{/* Proposed Fix */}
			{proposedFix && (
				<Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-950/40/30">
					<CardHeader className="pb-3">
						<CardTitle className="text-sm font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
							<div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
								<Wrench className="w-4 h-4 text-green-600 dark:text-green-400" />
							</div>
							Recommended Action
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0 space-y-3">
						<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
							<p className="text-sm text-green-800 dark:text-green-300 font-medium">
								{proposedFix.recommended_action}
							</p>
						</div>
						{proposedFix.reasoning && (
							<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
								<p className="text-sm text-green-800 dark:text-green-300">{proposedFix.reasoning}</p>
							</div>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
